import { useEffect, useState } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import ComponentCircleLoader from "../../../components/Loaders/ComponentCircleLoader";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0px;
  gap: 8px;
  width: 180px;
  min-height: 400px;
  max-height: 670px;
  background: #ffffff;
  border: 1px solid #b4c6f9;
  border-radius: 8px;
  flex: none;
  align-self: stretch;
  z-index: 1;
`;

const Header = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 8px;
  gap: 8px;
  width: 100%;
  height: 36px;
  background: #e9f0fe;
  border-bottom: 1px solid #b4c6f9;
  border-radius: 8px 8px 0px 0px;
  flex: none;
  align-self: stretch;
`;

const HeaderTitle = styled.h3`
  margin: 0;
  width: 100%;
  height: 20px;
  font-family: 'Mona Sans', sans-serif;
  font-style: normal;
  font-weight: 600;
  font-size: 14px;
  line-height: 20px;
  color: #000e2a;
  flex: 1;
  text-align: center;
`;

const JobListWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0px;
  gap: 6px;
  width: 100%;
  overflow-y: auto;
  flex: 1;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: #b4c6f9;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #8da9f0;
  }
`;

const JobItem = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 4px;
  gap: 4px;
  width: 100%;
  min-height: 32px;
  background: ${props => (props.checked ? "#edf8fe" : "#efefef")};
  border-width: ${props => (props.checked ? "1px 0px" : "0")};
  border-style: solid;
  border-color: #d3e0fc;
  flex: none;
  align-self: stretch;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background: ${props => (props.checked ? "#d9ebf9" : "#e0e0e0")};
  }
`;

const CheckboxWrapper = styled.div`
  width: 16px;
  height: 16px;
  flex: none;
  position: relative;
`;

const Checkbox = styled.input`
  width: 16px;
  height: 16px;
  cursor: pointer;
  position: absolute;
  opacity: 0;
  z-index: 1;

  &:checked + label {
    background: #2266ef;
    border-color: #2266ef;
  }

  &:checked + label::after {
    content: "";
    position: absolute;
    left: 5px;
    top: 2px;
    width: 4px;
    height: 8px;
    border: solid white;
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
  }
`;

const CheckboxLabel = styled.label`
  position: absolute;
  width: 16px;
  height: 16px;
  background: #ffffff;
  border: 1px solid #666e7f;
  border-radius: 2px;
  cursor: pointer;
  transition: all 0.2s ease;
`;

const JobName = styled.span`
  width: 100%;
  font-family: 'Mona Sans', sans-serif;
  font-style: normal;
  font-weight: 500;
  font-size: 12px;
  line-height: 17px;
  color: #000e2a;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const LoaderWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 40px 0;
`;

const EmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 40px 16px;
  text-align: center;
  font-family: 'Mona Sans', sans-serif;
  font-size: 12px;
  color: #666e7f;
`;

const JobListInMapContainer = ({
  jobList = [],
  isLoadingJobs = false,
  selectedJobs = [],
  onJobToggle,
  isOperationType,
}) => {
  const { t } = useTranslation();

  const handleJobToggle = (jobId) => {
    if (onJobToggle) {
      onJobToggle(jobId);
    }
  };

  const isJobSelected = (jobId) => {
    return selectedJobs.includes(jobId);
  };

  return (
    <Container>
      <Header>
        <HeaderTitle>{t(isOperationType ? "tasks_list" : "jobs_list") || (isOperationType ? "Tasks List" : "Jobs List")}</HeaderTitle>
      </Header>

      <JobListWrapper>
        {isLoadingJobs ? (
          <LoaderWrapper>
            <ComponentCircleLoader />
          </LoaderWrapper>
        ) : jobList.length === 0 ? (
          <EmptyState>
            {t(isOperationType ? "no_tasks_found" : "no_jobs_found") || (isOperationType ? "No tasks found for selected date" : "No jobs found for selected date")}
          </EmptyState>
        ) : (
          jobList.map((job) => {
            const isSelected = isJobSelected(job.id);
            return (
              <JobItem
                key={job.id}
                checked={isSelected}
                onClick={() => handleJobToggle(job.id)}
                title={job.name || job.job_code || "Unnamed Job"}
              >
                <CheckboxWrapper>
                  <Checkbox
                    type="checkbox"
                    id={`job-${job.id}`}
                    checked={isSelected}
                    onChange={() => handleJobToggle(job.id)}
                    onClick={() => handleJobToggle(job.id)}
                  />
                  <CheckboxLabel htmlFor={`job-${job.id}`} />
                </CheckboxWrapper>
                <JobName>{job.name || job.job_code || "Unnamed Job"}</JobName>
              </JobItem>
            );
          })
        )}
      </JobListWrapper>
    </Container>
  );
};

export default JobListInMapContainer;
